import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  FiAlertCircle, FiArrowRight, FiBriefcase, FiCheck, FiClock, FiCreditCard, FiDownload, FiEdit3, FiFile, FiFilter,
  FiMessageSquare, FiPaperclip, FiRefreshCw, FiSearch, FiSend, FiSliders, FiTrash2, FiX,
} from 'react-icons/fi';
import { api } from '../services/api';
import { formatServiceOrderAmount, formatServiceOrderDate, paymentStatusLabel, serviceOrderId, SERVICE_ORDER_STATUSES, statusInfo } from '../utils/serviceOrders';
import { getSupportSubscriptionTiming } from '../utils/userDashboard';
import './AdminServiceOrders.css';

const emptyEditor = () => ({
  status: 'new',
  progress: 0,
  priority: 'normal',
  assignedTo: '',
  dueDate: '',
  secondPaymentDueAt: '',
  internalNote: '',
  customerNote: '',
  visibleToCustomer: true,
  brief: {
    projectTitle: '',
    description: '',
    targetDate: '',
    budgetRange: '',
    referenceLinks: '',
  },
  quote: { amount: '', duration: '', details: '', validUntil: '', requiresCustomerApproval: true },
});
const dateInput = (value) => value ? new Date(value).toISOString().slice(0, 10) : '';
const emptyManualPayment = (amount = '') => ({
  amount,
  paidAt: dateInput(new Date()),
  paymentMethod: 'card_to_card',
  reference: '',
  note: '',
});
const DEFAULT_PAYMENT_MODES = ['full', 'two_stage'];
const selectionFromOrder = (order = {}) => ({
  serviceId: order.service?._id || order.service || '',
  groupId: order.planSnapshot?.groupId || '',
  planId: order.planSnapshot?.planId || '',
  durationMonths: order.planSnapshot?.durationMonths || '',
});
const paymentModesFromOrder = (order = {}) => {
  const modes = Array.isArray(order.paymentPolicy?.allowedModes)
    ? order.paymentPolicy.allowedModes.filter((mode) => DEFAULT_PAYMENT_MODES.includes(mode))
    : [];
  return modes.length ? modes : DEFAULT_PAYMENT_MODES;
};

const SERVICE_PAYMENT_EDIT_LOCK_MS = 30 * 60 * 1000;
const paymentEditLockFromOrder = (order = {}, now = Date.now()) => {
  const serverRetryAt = order.adminPaymentEditLock?.retryAvailableAt;
  let retryAtMs = serverRetryAt ? new Date(serverRetryAt).getTime() : NaN;

  if (!Number.isFinite(retryAtMs)) {
    const activeStage = (order.paymentSchedule?.installments || [])
      .filter((stage) => stage.status === 'processing' && stage.requestedAt)
      .map((stage) => new Date(stage.requestedAt).getTime())
      .filter(Number.isFinite)
      .sort((a, b) => b - a)[0];
    if (Number.isFinite(activeStage)) retryAtMs = activeStage + SERVICE_PAYMENT_EDIT_LOCK_MS;
  }

  const remainingSeconds = Number.isFinite(retryAtMs)
    ? Math.max(0, Math.ceil((retryAtMs - now) / 1000))
    : 0;
  return {
    locked: remainingSeconds > 0,
    retryAtMs: Number.isFinite(retryAtMs) ? retryAtMs : null,
    remainingSeconds,
  };
};
const paymentLockCountdown = (seconds = 0) => {
  const total = Math.max(0, Number(seconds) || 0);
  const minutes = Math.floor(total / 60);
  const secs = total % 60;
  return `${minutes.toLocaleString('fa-IR')}:${String(secs).padStart(2, '0').replace(/\d/g, (digit) => '۰۱۲۳۴۵۶۷۸۹'[Number(digit)])}`;
};
const selectionMatchesOrder = (requested = {}, order = {}) => {
  const saved = selectionFromOrder(order);
  const sameBase = String(saved.serviceId || '') === String(requested.serviceId || '')
    && String(saved.groupId || '') === String(requested.groupId || '')
    && String(saved.planId || '') === String(requested.planId || '');
  if (!sameBase) return false;
  const requestedDuration = Number(requested.durationMonths || 0);
  const savedDuration = Number(saved.durationMonths || 0);
  return requestedDuration === savedDuration;
};

const supportTimingLabel = (timing) => {
  if (!timing) return '—';
  if (timing.status === 'cancelled') return 'لغوشده';
  if (timing.status === 'suspended') return 'معلق';
  if (timing.status === 'inactive') return 'در انتظار شروع';
  if (timing.status === 'expired' || timing.daysRemaining <= 0) return 'پایان‌یافته';
  return `${timing.daysRemaining.toLocaleString('fa-IR')} روز باقی‌مانده`;
};

const AdminServiceOrders = ({
  onCountChange,
  initialOrderId = '',
  onInitialOrderHandled,
}) => {
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [editor, setEditor] = useState(emptyEditor());
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState(null);
  const [message, setMessage] = useState('');
  const [messageFiles, setMessageFiles] = useState([]);
  const [manualPayment, setManualPayment] = useState(emptyManualPayment());
  const [serviceSelection, setServiceSelection] = useState(selectionFromOrder());
  const [allowedPaymentModes, setAllowedPaymentModes] = useState(DEFAULT_PAYMENT_MODES);
  const [activeDetailTab, setActiveDetailTab] = useState('project');
  const [paymentLockNow, setPaymentLockNow] = useState(Date.now());

  const load = async () => {
    setLoading(true);
    try {
      const [orderResponse, userResponse, serviceResponse] = await Promise.all([
        api.get('/api/service-orders/admin/list'),
        api.get('/api/users'),
        api.get('/api/services/admin/list'),
      ]);
      const list = Array.isArray(orderResponse.data) ? orderResponse.data : [];
      setOrders(list); onCountChange?.(list.length);
      setUsers((userResponse.data || []).filter((user) => user.role === 'admin'));
      setServices(Array.isArray(serviceResponse.data) ? serviceResponse.data : []);
      if (selected) setSelected(list.find((item) => item._id === selected._id) || null);
    } catch (error) { setNotice({ type: 'error', text: error.data?.message || error.message || 'دریافت سفارش‌ها انجام نشد' }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const open = useCallback(async (order) => {
    setBusy('detail'); setNotice(null); setActiveDetailTab('project');
    try {
      const response = await api.get(`/api/service-orders/${order._id}`);
      setSelected(response.data);
      setServiceSelection(selectionFromOrder(response.data));
      setAllowedPaymentModes(paymentModesFromOrder(response.data));
      const nextPayment = response.data.paymentSchedule?.installments?.find(
        (stage) => stage.status !== 'paid'
      );
      setManualPayment(emptyManualPayment(
        nextPayment?.amount || response.data.paymentSchedule?.remainingAmount || ''
      ));
      setEditor({
        status: response.data.status, progress: response.data.progress || 0, priority: response.data.priority || 'normal', assignedTo: response.data.assignedTo?._id || '', dueDate: dateInput(response.data.dueDate), secondPaymentDueAt: dateInput(response.data.paymentSchedule?.secondPaymentDueAt || response.data.paymentSchedule?.installments?.find((stage) => Number(stage.number) === 2)?.dueAt), internalNote: response.data.internalNote || '', customerNote: '', visibleToCustomer: true,
        brief: {
          projectTitle: response.data.brief?.projectTitle || '',
          description: response.data.brief?.description || '',
          targetDate: dateInput(response.data.brief?.targetDate),
          budgetRange: response.data.brief?.budgetRange || '',
          referenceLinks: (response.data.brief?.referenceLinks || []).join('\n'),
        },
        quote: { amount: response.data.quote?.amount || '', duration: response.data.quote?.duration || '', details: response.data.quote?.details || '', validUntil: dateInput(response.data.quote?.validUntil), requiresCustomerApproval: response.data.quote?.requiresCustomerApproval !== false },
      });
    } catch (error) { setNotice({ type: 'error', text: error.data?.message || error.message }); }
    finally { setBusy(''); }
  }, []);


  const refreshSelectedOrder = useCallback(async (orderId) => {
    if (!orderId) return null;
    const response = await api.get(`/api/service-orders/${orderId}`);
    const fresh = response.data;
    setSelected(fresh);
    setOrders((current) => current.map((item) => item._id === fresh._id ? fresh : item));
    setServiceSelection(selectionFromOrder(fresh));
    setAllowedPaymentModes(paymentModesFromOrder(fresh));
    setPaymentLockNow(Date.now());
    return fresh;
  }, []);

  useEffect(() => {
    if (!initialOrderId) return;
    open({ _id: initialOrderId }).finally(() => onInitialOrderHandled?.());
  }, [initialOrderId, onInitialOrderHandled, open]);

  const filtered = useMemo(() => orders.filter((order) => {
    if (statusFilter && order.status !== statusFilter) return false;
    const haystack = [order.orderNumber, order.customer?.name, order.customer?.email, order.serviceSnapshot?.title, order.brief?.projectTitle, statusInfo(order.status).label].join(' ').toLowerCase();
    return haystack.includes(search.toLowerCase());
  }), [orders, search, statusFilter]);

  const selectedServiceConfig = useMemo(() => services.find(
    (service) => String(service._id) === String(serviceSelection.serviceId)
  ) || null, [services, serviceSelection.serviceId]);
  const selectedGroupConfig = useMemo(() => (selectedServiceConfig?.planGroups || []).find(
    (group) => String(group.id) === String(serviceSelection.groupId)
  ) || null, [selectedServiceConfig, serviceSelection.groupId]);
  const selectionNeedsDuration = selectedServiceConfig?.slug === 'website-support'
    && selectedGroupConfig?.id === 'monthly-support'
    && selectedGroupConfig?.mode !== 'request';
  const paymentPolicyLocked = Number(selected?.paymentSchedule?.paidAmount || 0) > 0
    || ['paid', 'partially_paid', 'refunded'].includes(selected?.paymentStatus);

  const pendingPaymentEditLock = useMemo(
    () => paymentEditLockFromOrder(selected || {}, paymentLockNow),
    [selected, paymentLockNow]
  );
  const financialEditLocked = paymentPolicyLocked || pendingPaymentEditLock.locked;

  useEffect(() => {
    setPaymentLockNow(Date.now());
  }, [selected?._id, selected?.adminPaymentEditLock?.retryAvailableAt]);

  useEffect(() => {
    if (!selected || !pendingPaymentEditLock.locked) return undefined;
    const timer = window.setInterval(() => setPaymentLockNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [selected?._id, pendingPaymentEditLock.locked, pendingPaymentEditLock.retryAtMs]);

  const onServiceSelectionChange = (serviceId) => {
    const service = services.find((item) => String(item._id) === String(serviceId));
    const group = (service?.planGroups || [])[0] || null;
    const plan = group?.mode === 'request' ? null : (group?.plans || [])[0] || null;
    const duration = service?.slug === 'website-support' && group?.id === 'monthly-support'
      ? (group?.durationOptions || [])[0]?.months || ''
      : '';
    setServiceSelection({ serviceId, groupId: group?.id || '', planId: plan?.id || '', durationMonths: duration });
  };

  const onGroupSelectionChange = (groupId) => {
    const group = (selectedServiceConfig?.planGroups || []).find((item) => String(item.id) === String(groupId));
    const plan = group?.mode === 'request' ? null : (group?.plans || [])[0] || null;
    const duration = selectedServiceConfig?.slug === 'website-support' && group?.id === 'monthly-support'
      ? (group?.durationOptions || [])[0]?.months || ''
      : '';
    setServiceSelection((current) => ({ ...current, groupId, planId: plan?.id || '', durationMonths: duration }));
  };

  const save = async ({ sendQuote = false } = {}) => {
    setBusy(sendQuote ? 'quote' : 'save'); setNotice(null);
    try {
      const response = await api.put(`/api/service-orders/${selected._id}/admin`, { ...editor, sendQuote, quote: { ...editor.quote, amount: Number(String(editor.quote.amount).replace(/,/g, '')) || 0 } });
      setSelected(response.data);
      setOrders((current) => current.map((item) => item._id === response.data._id ? response.data : item));
      setEditor((current) => ({ ...current, status: response.data.status, customerNote: '' }));
      setNotice({ type: 'success', text: response.message });
    } catch (error) { setNotice({ type: 'error', text: error.data?.message || error.message }); }
    finally { setBusy(''); }
  };

  const saveServiceSelection = async () => {
    if (!serviceSelection.serviceId || !serviceSelection.groupId) {
      setNotice({ type: 'error', text: 'خدمت و نوع درخواست/گروه را انتخاب کنید' });
      return;
    }
    if (selectedGroupConfig?.mode !== 'request' && !serviceSelection.planId) {
      setNotice({ type: 'error', text: 'پلن خدمت را انتخاب کنید' });
      return;
    }
    if (selectionNeedsDuration && !serviceSelection.durationMonths) {
      setNotice({ type: 'error', text: 'مدت قرارداد پشتیبانی را انتخاب کنید' });
      return;
    }
    setBusy('selection'); setNotice(null);
    try {
      const requestedSelection = { ...serviceSelection };
      const response = await api.patch(`/api/service-orders/${selected._id}/admin/selection`, requestedSelection);
      if (!response?.success || !response?.data || !selectionMatchesOrder(requestedSelection, response.data)) {
        throw new Error('سرور تغییر خدمت و پلن را تأیید نکرد؛ اطلاعات سفارش بدون تغییر باقی ماند');
      }
      setSelected(response.data);
      setOrders((current) => current.map((item) => item._id === response.data._id ? response.data : item));
      setServiceSelection(selectionFromOrder(response.data));
      setAllowedPaymentModes(paymentModesFromOrder(response.data));
      setEditor((current) => ({
        ...current,
        status: response.data.status,
        secondPaymentDueAt: dateInput(response.data.paymentSchedule?.secondPaymentDueAt || response.data.paymentSchedule?.installments?.find((stage) => Number(stage.number) === 2)?.dueAt),
        quote: {
          amount: response.data.quote?.amount || '',
          duration: response.data.quote?.duration || '',
          details: response.data.quote?.details || '',
          validUntil: dateInput(response.data.quote?.validUntil),
          requiresCustomerApproval: response.data.quote?.requiresCustomerApproval !== false,
        },
      }));
      const nextPayment = response.data.paymentSchedule?.installments?.find((stage) => stage.status !== 'paid');
      setManualPayment(emptyManualPayment(nextPayment?.amount || response.data.paymentSchedule?.remainingAmount || ''));
      setNotice({ type: 'success', text: response.message });
    } catch (error) {
      if (error.data?.code === 'SERVICE_PAYMENT_CONFIRMED') {
        try { await refreshSelectedOrder(selected?._id); } catch (_) { /* پیام اصلی خطا حفظ می‌شود */ }
      }
      if (String(error.data?.code || '').startsWith('SERVICE_PAYMENT_') && error.data?.retryAvailableAt) {
        setSelected((current) => current ? {
          ...current,
          adminPaymentEditLock: {
            locked: true,
            code: error.data.code,
            retryAvailableAt: error.data.retryAvailableAt,
            retryAfterSeconds: error.data.retryAfterSeconds || 0,
          },
        } : current);
        setPaymentLockNow(Date.now());
      }
      setNotice({ type: 'error', text: error.data?.message || error.message || 'اصلاح خدمت و پلن انجام نشد' });
    } finally { setBusy(''); }
  };

  const toggleAllowedPaymentMode = (mode) => {
    setAllowedPaymentModes((current) => {
      if (current.includes(mode)) {
        if (current.length === 1) return current;
        return current.filter((item) => item !== mode);
      }
      return [...current, mode];
    });
  };

  const savePaymentPolicy = async () => {
    setBusy('payment-policy'); setNotice(null);
    try {
      const response = await api.patch(`/api/service-orders/${selected._id}/admin/payment-policy`, {
        allowedModes: allowedPaymentModes,
      });
      setSelected(response.data);
      setOrders((current) => current.map((item) => item._id === response.data._id ? response.data : item));
      setAllowedPaymentModes(paymentModesFromOrder(response.data));
      setNotice({ type: 'success', text: response.message });
    } catch (error) {
      if (error.data?.code === 'SERVICE_PAYMENT_CONFIRMED') {
        try { await refreshSelectedOrder(selected?._id); } catch (_) { /* پیام اصلی خطا حفظ می‌شود */ }
      }
      if (String(error.data?.code || '').startsWith('SERVICE_PAYMENT_') && error.data?.retryAvailableAt) {
        setSelected((current) => current ? {
          ...current,
          adminPaymentEditLock: {
            locked: true,
            code: error.data.code,
            retryAvailableAt: error.data.retryAvailableAt,
            retryAfterSeconds: error.data.retryAfterSeconds || 0,
          },
        } : current);
        setPaymentLockNow(Date.now());
      }
      setNotice({ type: 'error', text: error.data?.message || error.message || 'ذخیره روش‌های پرداخت مجاز انجام نشد' });
    } finally { setBusy(''); }
  };

  const sendMessage = async (event) => {
    event.preventDefault(); if (!message.trim() && !messageFiles.length) return;
    setBusy('message');
    try {
      const body = new FormData(); body.append('text', message); messageFiles.forEach((file) => body.append('attachments', file));
      const response = await api.upload(`/api/service-orders/${selected._id}/messages`, body);
      setSelected(response.data); setMessage(''); setMessageFiles([]); setNotice({ type: 'success', text: 'پیام برای مشتری ارسال شد' });
    } catch (error) { setNotice({ type: 'error', text: error.data?.message || error.message }); }
    finally { setBusy(''); }
  };

  const recordManualPayment = async (event) => {
    event.preventDefault();
    const amount = Number(String(manualPayment.amount).replace(/[,٬،\s]/g, ''));
    if (!(amount > 0)) {
      setNotice({ type: 'error', text: 'مبلغ پرداخت‌شده را وارد کنید' });
      return;
    }
    setBusy('manual-payment');
    setNotice(null);
    try {
      const response = await api.post(`/api/service-orders/${selected._id}/manual-payments`, {
        ...manualPayment,
        amount,
      });
      setSelected(response.data);
      setOrders((current) => current.map((item) => (
        item._id === response.data._id ? response.data : item
      )));
      const nextPayment = response.data.paymentSchedule?.installments?.find(
        (stage) => stage.status !== 'paid'
      );
      setManualPayment(emptyManualPayment(
        nextPayment?.amount || response.data.paymentSchedule?.remainingAmount || ''
      ));
      setNotice({ type: 'success', text: response.message });
    } catch (error) {
      setNotice({ type: 'error', text: error.data?.message || error.message || 'ثبت پرداخت دستی انجام نشد' });
    } finally {
      setBusy('');
    }
  };

  const download = async (attachment) => {
    setBusy(attachment._id);
    try {
      const result = await api.download(`/api/service-orders/${selected._id}/attachments/${attachment._id}`);
      const url = URL.createObjectURL(result.blob); const link = document.createElement('a'); link.href = url; link.download = result.filename || attachment.originalName; document.body.appendChild(link); link.click(); link.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) { setNotice({ type: 'error', text: error.data?.message || error.message }); }
    finally { setBusy(''); }
  };

  const remove = async (order) => {
    const id = order?._id;
    if (!id || !window.confirm(
      `سفارش «${order.brief?.projectTitle || serviceOrderId(order)}» کاملاً حذف شود؟\n\nتمام تراکنش‌ها، گفتگوها و فایل‌های وابسته نیز حذف می‌شوند و این عملیات قابل بازگشت نیست.`
    )) return;
    setBusy(`delete:${id}`); setNotice(null);
    try {
      const response = await api.delete(`/api/service-orders/${id}`);
      const nextOrders = orders.filter((item) => item._id !== id);
      setOrders(nextOrders);
      onCountChange?.(nextOrders.length);
      if (selected?._id === id) setSelected(null);
      setNotice({ type: 'success', text: response.message });
    } catch (error) {
      setNotice({ type: 'error', text: error.data?.message || error.message || 'حذف سفارش انجام نشد' });
    } finally {
      setBusy('');
    }
  };

  const secondPaymentStage = selected?.paymentSchedule?.installments?.find(
    (stage) => Number(stage.number) === 2
  );
  const selectedSupportTiming = getSupportSubscriptionTiming(selected);
  const detailTabs = selected ? [
    {
      id: 'project',
      label: 'پروژه',
      hint: 'اجرا و جزئیات',
      icon: <FiBriefcase />,
    },
    {
      id: 'finance',
      label: 'مالی',
      hint: 'قیمت و پرداخت',
      icon: <FiCreditCard />,
      badge: Number(selected.paymentSchedule?.remainingAmount || 0) > 0 ? 'مانده' : null,
    },
    {
      id: 'request',
      label: 'درخواست و فایل‌ها',
      hint: 'اطلاعات اولیه',
      icon: <FiFile />,
      badge: (selected.attachments || []).length || null,
    },
    {
      id: 'history',
      label: 'تاریخچه',
      hint: 'رویدادهای سفارش',
      icon: <FiRefreshCw />,
      badge: (selected.timeline || []).length || null,
    },
    {
      id: 'conversation',
      label: 'گفتگو',
      hint: 'ارتباط با مشتری',
      icon: <FiMessageSquare />,
      badge: (selected.messages || []).length || null,
    },
  ] : [];

  const handleDetailTabKeyDown = (event, currentIndex) => {
    const lastIndex = detailTabs.length - 1;
    let nextIndex = null;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = lastIndex;
    if (event.key === 'ArrowLeft') nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
    if (event.key === 'ArrowRight') nextIndex = currentIndex === 0 ? lastIndex : currentIndex - 1;
    if (nextIndex === null) return;
    event.preventDefault();
    const nextTab = detailTabs[nextIndex];
    setActiveDetailTab(nextTab.id);
    requestAnimationFrame(() => {
      document.getElementById(`admin-service-order-tab-${nextTab.id}`)?.focus();
    });
  };

  useEffect(() => {
    if (!selected) return undefined;

    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setSelected(null);
    };

    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selected]);

  const detailModal = (selected || busy === 'detail') && createPortal(
    <div
      className="admin-form-modal admin-service-order-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="admin-service-order-modal-title"
      dir="rtl"
    >
      <div className="admin-form-shell">
        <header className="admin-form-header">
          <div className="admin-form-heading">
            <span className="admin-form-icon" aria-hidden="true"><FiBriefcase /></span>
            <div>
              <span className="admin-form-eyebrow">
                {selected
                  ? `سفارش #${serviceOrderId(selected)} · ${formatServiceOrderDate(selected.createdAt)}`
                  : 'مدیریت سفارش خدمات'}
              </span>
              <h3 id="admin-service-order-modal-title">
                {selected?.brief?.projectTitle || 'در حال دریافت جزئیات سفارش...'}
              </h3>
              {selected && (
                <p>
                  {selected.customer?.name || 'مشتری'} · {selected.serviceSnapshot?.title || 'خدمت ثبت‌شده'}
                </p>
              )}
            </div>
          </div>
          <div className="admin-service-order-header-actions">
            {selected && (
              <span className={`service-order-status is-${statusInfo(selected.status).tone}`}>
                {statusInfo(selected.status).label}
              </span>
            )}
            <button
              type="button"
              className="admin-form-close ui-close-button"
              onClick={() => setSelected(null)}
              aria-label="بستن مدیریت سفارش"
            >
              <FiX aria-hidden="true" />
            </button>
          </div>
        </header>

        <main className="admin-form-content admin-service-order-modal-content">
          {busy === 'detail' && !selected ? (
            <div className="admin-service-order-loading">
              <FiRefreshCw aria-hidden="true" />
              <strong>در حال دریافت جزئیات سفارش...</strong>
            </div>
          ) : selected && (
            <div className="admin-entity-form admin-service-order-workspace">
              {notice && (
                <div className={`admin-service-order-notice is-${notice.type}`}>
                  <FiAlertCircle /> {notice.text}
                </div>
              )}

              <div className="admin-form-note admin-service-order-intro">
                <FiBriefcase aria-hidden="true" />
                <div>
                  <strong>بررسی و مدیریت سفارش خدمات</strong>
                  <span>اطلاعات مشتری، اجرای پروژه، پیشنهاد قیمت، پرداخت‌ها و گفتگوها از همین صفحه مدیریت می‌شوند.</span>
                </div>
              </div>

              <div className={`admin-service-order-overview ${selectedSupportTiming ? 'has-support-timing' : ''}`} aria-label="خلاصه سفارش">
                <div>
                  <small>مشتری</small>
                  <strong>{selected.customer?.name || '—'}</strong>
                  <span>{selected.customer?.phone || selected.customer?.email || 'بدون اطلاعات تماس'}</span>
                </div>
                <div>
                  <small>{selected.planSnapshot?.selectionType === 'quote_request' ? 'خدمت و نوع درخواست' : 'خدمت و پلن'}</small>
                  <strong>{selected.serviceSnapshot?.title || '—'}</strong>
                  <span>{selected.planSnapshot?.name || 'انتخاب مشخص نشده'}</span>
                </div>
                <div>
                  <small>پیشرفت پروژه</small>
                  <strong>{Number(editor.progress || 0).toLocaleString('fa-IR')}٪</strong>
                  <span>{editor.dueDate ? `تحویل هدف: ${formatServiceOrderDate(editor.dueDate)}` : 'تاریخ تحویل تعیین نشده'}</span>
                </div>
                <div>
                  <small>وضعیت پرداخت</small>
                  <strong>{paymentStatusLabel(selected.paymentStatus)}</strong>
                  <span>
                    {selected.paymentSchedule?.totalAmount
                      ? `${formatServiceOrderAmount(selected.paymentSchedule.paidAmount)} از ${formatServiceOrderAmount(selected.paymentSchedule.totalAmount)} ${selected.currencyLabel || 'تومان'}`
                      : 'مبلغ قرارداد ثبت نشده'}
                  </span>
                </div>
                {selectedSupportTiming && (
                  <div className={`admin-service-order-support-overview is-${selectedSupportTiming.status}`}>
                    <small><FiClock /> زمان پشتیبانی</small>
                    <strong>{supportTimingLabel(selectedSupportTiming)}</strong>
                    <span>پایان: {formatServiceOrderDate(selectedSupportTiming.endsAt)}</span>
                    <i aria-hidden="true">
                      <b style={{ width: `${selectedSupportTiming.remainingPercent}%` }} />
                    </i>
                  </div>
                )}
              </div>

              <div className="admin-service-order-tabs-shell">
                <nav className="admin-service-order-tabs" role="tablist" aria-label="بخش‌های مدیریت سفارش">
                  {detailTabs.map((tab, index) => (
                    <button
                      key={tab.id}
                      type="button"
                      id={`admin-service-order-tab-${tab.id}`}
                      className={activeDetailTab === tab.id ? 'is-active' : ''}
                      role="tab"
                      aria-selected={activeDetailTab === tab.id}
                      aria-controls={`admin-service-order-panel-${tab.id}`}
                      tabIndex={activeDetailTab === tab.id ? 0 : -1}
                      onClick={() => setActiveDetailTab(tab.id)}
                      onKeyDown={(event) => handleDetailTabKeyDown(event, index)}
                    >
                      <span className="admin-service-order-tab-icon" aria-hidden="true">{tab.icon}</span>
                      <span className="admin-service-order-tab-copy">
                        <strong>{tab.label}</strong>
                        <small>{tab.hint}</small>
                      </span>
                      {tab.badge !== null && tab.badge !== undefined && (
                        <span className="admin-service-order-tab-badge">
                          {typeof tab.badge === 'number' ? tab.badge.toLocaleString('fa-IR') : tab.badge}
                        </span>
                      )}
                    </button>
                  ))}
                </nav>
              </div>

              <section
                id="admin-service-order-panel-project"
                className="admin-service-order-tab-panel"
                role="tabpanel"
                aria-labelledby="admin-service-order-tab-project"
                hidden={activeDetailTab !== 'project'}
              >
                <div className="admin-service-order-panel-heading">
                  <div>
                    <span>مدیریت اجرایی</span>
                    <h3>پروژه و اطلاعات سفارش</h3>
                    <p>وضعیت اجرا، مسئول پروژه و اطلاعاتی که در طول انجام کار نیاز دارید.</p>
                  </div>
                  <span className={`service-order-status is-${statusInfo(editor.status).tone}`}>
                    {statusInfo(editor.status).label}
                  </span>
                </div>

                <div className="admin-service-order-card-grid">
                  <section className="admin-service-order-card is-half">
                    <div className="admin-service-order-card-heading">
                      <span><FiCheck /></span>
                      <div>
                        <h3>مدیریت پروژه</h3>
                        <p>وضعیت اجرا، مسئول پروژه و زمان‌بندی را به‌روزرسانی کنید.</p>
                      </div>
                    </div>
                    <div className="admin-service-order-fields">
                      <label>
                        وضعیت
                        <select value={editor.status} onChange={(event) => setEditor({ ...editor, status: event.target.value })}>
                          {Object.entries(SERVICE_ORDER_STATUSES).map(([id, info]) => <option value={id} key={id}>{info.label}</option>)}
                        </select>
                      </label>
                      <label>
                        اولویت
                        <select value={editor.priority} onChange={(event) => setEditor({ ...editor, priority: event.target.value })}>
                          <option value="normal">عادی</option>
                          <option value="high">بالا</option>
                          <option value="urgent">فوری</option>
                        </select>
                      </label>
                      <label className="is-wide">
                        درصد پیشرفت
                        <div className="admin-progress-input">
                          <input type="range" min="0" max="100" value={editor.progress} onChange={(event) => setEditor({ ...editor, progress: Number(event.target.value) })} />
                          <strong>{Number(editor.progress).toLocaleString('fa-IR')}٪</strong>
                        </div>
                      </label>
                      <label>
                        مسئول پروژه
                        <select value={editor.assignedTo} onChange={(event) => setEditor({ ...editor, assignedTo: event.target.value })}>
                          <option value="">تعیین نشده</option>
                          {users.map((user) => <option value={user._id || user.id} key={user._id || user.id}>{user.name}</option>)}
                        </select>
                      </label>
                      <label>
                        تاریخ تحویل هدف
                        <input type="date" value={editor.dueDate} onChange={(event) => setEditor({ ...editor, dueDate: event.target.value })} />
                      </label>
                      <label className="is-wide">
                        پیام همراه تغییر وضعیت
                        <textarea rows="3" value={editor.customerNote} onChange={(event) => setEditor({ ...editor, customerNote: event.target.value })} />
                      </label>
                      <label className="admin-inline-check is-wide">
                        <input type="checkbox" checked={editor.visibleToCustomer} onChange={(event) => setEditor({ ...editor, visibleToCustomer: event.target.checked })} />
                        نمایش این تغییر در تایم‌لاین مشتری
                      </label>
                      <label className="is-wide">
                        یادداشت داخلی
                        <textarea rows="4" value={editor.internalNote} onChange={(event) => setEditor({ ...editor, internalNote: event.target.value })} placeholder="فقط مدیران مشاهده می‌کنند" />
                      </label>
                    </div>
                  </section>

                  <section className="admin-service-order-card is-half admin-service-brief-editor">
                    <div className="admin-service-order-card-heading">
                      <span><FiBriefcase /></span>
                      <div>
                        <h3>اطلاعات سفارش</h3>
                        <p>عنوان و جزئیات اجرایی پروژه را ویرایش کنید.</p>
                      </div>
                    </div>
                    <div className="admin-service-order-fields">
                      <label className="is-wide">
                        عنوان پروژه
                        <input value={editor.brief.projectTitle} onChange={(event) => setEditor({ ...editor, brief: { ...editor.brief, projectTitle: event.target.value } })} />
                      </label>
                      <label className="is-wide">
                        شرح پروژه
                        <textarea rows="5" value={editor.brief.description} onChange={(event) => setEditor({ ...editor, brief: { ...editor.brief, description: event.target.value } })} />
                      </label>
                      <label>
                        تاریخ مطلوب مشتری
                        <input type="date" value={editor.brief.targetDate} onChange={(event) => setEditor({ ...editor, brief: { ...editor.brief, targetDate: event.target.value } })} />
                      </label>
                      <label>
                        بودجه اعلامی
                        <input value={editor.brief.budgetRange} onChange={(event) => setEditor({ ...editor, brief: { ...editor.brief, budgetRange: event.target.value } })} />
                      </label>
                      <label className="is-wide">
                        لینک‌های مرجع
                        <textarea rows="3" value={editor.brief.referenceLinks} onChange={(event) => setEditor({ ...editor, brief: { ...editor.brief, referenceLinks: event.target.value } })} placeholder="هر لینک در یک خط" />
                      </label>
                    </div>
                  </section>
                </div>

                <section className="admin-service-order-card is-full admin-service-selection-editor">
                  <div className="admin-service-order-card-heading">
                    <span><FiEdit3 /></span>
                    <div>
                      <h3>اصلاح خدمت و پلن مشتری</h3>
                      <p>اگر مشتری هنگام ثبت درخواست خدمت، نوع درخواست، پلن یا مدت قرارداد را اشتباه انتخاب کرده است، انتخاب صحیح را از اینجا جایگزین کنید.</p>
                    </div>
                  </div>
                  <div className="admin-service-selection-current">
                    <small>انتخاب فعلی سفارش</small>
                    <strong>{selected.serviceSnapshot?.title || '—'} · {selected.planSnapshot?.name || '—'}</strong>
                    {selected.planSnapshot?.period && <span>{selected.planSnapshot.period}</span>}
                  </div>
                  <div className="admin-service-order-fields">
                    <label>
                      خدمت
                      <select value={serviceSelection.serviceId} onChange={(event) => onServiceSelectionChange(event.target.value)}>
                        <option value="">انتخاب خدمت</option>
                        {services.map((service) => (
                          <option value={service._id} key={service._id}>
                            {service.title}{service.isActive === false ? ' (غیرفعال)' : ''}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      نوع خدمت / گروه
                      <select value={serviceSelection.groupId} onChange={(event) => onGroupSelectionChange(event.target.value)} disabled={!selectedServiceConfig}>
                        <option value="">انتخاب نوع خدمت</option>
                        {(selectedServiceConfig?.planGroups || []).map((group) => (
                          <option value={group.id} key={group.id}>{group.title}</option>
                        ))}
                      </select>
                    </label>
                    {selectedGroupConfig?.mode === 'request' ? (
                      <div className="admin-service-selection-request is-wide">
                        <small>نوع درخواست انتخاب‌شده</small>
                        <strong>{selectedGroupConfig.request?.title || selectedGroupConfig.title}</strong>
                        <span>{selectedGroupConfig.request?.badge || 'قیمت پس از بررسی مدیریت تعیین می‌شود.'}</span>
                      </div>
                    ) : (
                      <label>
                        پلن
                        <select value={serviceSelection.planId} onChange={(event) => setServiceSelection({ ...serviceSelection, planId: event.target.value })} disabled={!selectedGroupConfig}>
                          <option value="">انتخاب پلن</option>
                          {(selectedGroupConfig?.plans || []).map((plan) => (
                            <option value={plan.id} key={plan.id}>{plan.name}</option>
                          ))}
                        </select>
                      </label>
                    )}
                    {selectionNeedsDuration && (
                      <label>
                        مدت قرارداد پشتیبانی
                        <select value={serviceSelection.durationMonths} onChange={(event) => setServiceSelection({ ...serviceSelection, durationMonths: Number(event.target.value) })}>
                          <option value="">انتخاب مدت قرارداد</option>
                          {(selectedGroupConfig?.durationOptions || []).map((option) => (
                            <option value={option.months} key={option.months}>
                              {option.label}{option.discountPercent ? ` · ${option.discountPercent.toLocaleString('fa-IR')}٪ تخفیف` : ''}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                  </div>
                  {paymentPolicyLocked ? (
                    <div className="admin-service-selection-warning is-locked">
                      <FiAlertCircle />
                      <span>برای این سفارش پرداخت موفق ثبت شده است؛ برای حفظ سوابق مالی، خدمت و پلن دیگر قابل تغییر نیست.</span>
                    </div>
                  ) : pendingPaymentEditLock.locked ? (
                    <div className="admin-service-selection-warning is-pending-payment">
                      <FiClock />
                      <div>
                        <strong>نتیجه پرداخت مشتری هنوز در انتظار است</strong>
                        <span>تا پایان مهلت امن درگاه امکان اصلاح خدمت و پلن وجود ندارد. پس از پایان شمارش، وضعیت یک‌بار بررسی می‌شود؛ اگر واسط همچنان در انتظار باشد، تلاش قدیمی منقضی می‌شود و ویرایش آزاد خواهد شد.</span>
                        <b>{paymentLockCountdown(pendingPaymentEditLock.remainingSeconds)}</b>
                      </div>
                    </div>
                  ) : (
                    <div className="admin-service-selection-warning">
                      <FiAlertCircle />
                      <span>برای حفظ سوابق مالی، پس از ثبت اولین پرداخت موفق امکان تغییر خدمت یا پلن وجود ندارد.</span>
                    </div>
                  )}
                  <button type="button" className="admin-service-selection-submit" onClick={saveServiceSelection} disabled={busy === 'selection' || financialEditLocked}>
                    <FiCheck /> {busy === 'selection'
                      ? 'در حال اعمال تغییر...'
                      : paymentPolicyLocked
                        ? 'به دلیل ثبت پرداخت قفل شده است'
                        : pendingPaymentEditLock.locked
                          ? `در انتظار نتیجه پرداخت · ${paymentLockCountdown(pendingPaymentEditLock.remainingSeconds)}`
                          : 'اعمال خدمت و پلن جدید'}
                  </button>
                </section>

                <details className="admin-service-danger-disclosure">
                  <summary>تنظیمات بیشتر سفارش</summary>
                  <section className="admin-service-order-card is-full admin-service-danger-zone">
                    <div>
                      <h3><FiTrash2 /> حذف کامل سفارش</h3>
                      <p>سفارش همراه تمام تراکنش‌ها، گفتگوها و فایل‌های وابسته حذف می‌شود و قابل بازگشت نیست.</p>
                    </div>
                    <button type="button" onClick={() => remove(selected)} disabled={busy === `delete:${selected._id}`}>
                      <FiTrash2 /> {busy === `delete:${selected._id}` ? 'در حال حذف...' : 'حذف کامل سفارش'}
                    </button>
                  </section>
                </details>
              </section>

              <section
                id="admin-service-order-panel-finance"
                className="admin-service-order-tab-panel"
                role="tabpanel"
                aria-labelledby="admin-service-order-tab-finance"
                hidden={activeDetailTab !== 'finance'}
              >
                <div className="admin-service-order-panel-heading">
                  <div>
                    <span>امور مالی سفارش</span>
                    <h3>پیشنهاد قیمت و پرداخت‌ها</h3>
                    <p>مبلغ قرارداد، وضعیت تسویه و پرداخت‌های خارج از درگاه را از یک بخش مدیریت کنید.</p>
                  </div>
                  <strong className="admin-service-order-panel-metric">{paymentStatusLabel(selected.paymentStatus)}</strong>
                </div>

                <div className="admin-service-order-card-grid">
                  <section className="admin-service-order-card is-full admin-service-payment-policy">
                    <div className="admin-service-order-card-heading">
                      <span><FiSliders /></span>
                      <div>
                        <h3>روش‌های پرداخت مجاز مشتری</h3>
                        <p>مشخص کنید این مشتری اجازه پرداخت کامل، پرداخت دو مرحله‌ای یا هر دو روش را داشته باشد.</p>
                      </div>
                    </div>
                    <div className="admin-service-payment-policy-options">
                      <label className={allowedPaymentModes.includes('full') ? 'is-active' : ''}>
                        <input
                          type="checkbox"
                          checked={allowedPaymentModes.includes('full')}
                          onChange={() => toggleAllowedPaymentMode('full')}
                          disabled={financialEditLocked}
                        />
                        <span><FiCreditCard /></span>
                        <div>
                          <strong>پرداخت کامل</strong>
                          <small>کل مبلغ قرارداد در یک مرحله پرداخت می‌شود.</small>
                        </div>
                        <i>{allowedPaymentModes.includes('full') ? 'فعال' : 'غیرفعال'}</i>
                      </label>
                      <label className={allowedPaymentModes.includes('two_stage') ? 'is-active' : ''}>
                        <input
                          type="checkbox"
                          checked={allowedPaymentModes.includes('two_stage')}
                          onChange={() => toggleAllowedPaymentMode('two_stage')}
                          disabled={financialEditLocked}
                        />
                        <span><FiCreditCard /></span>
                        <div>
                          <strong>پرداخت اولیه و تسویه</strong>
                          <small>۵۰٪ ابتدا و ۵۰٪ مانده در مرحله دوم پرداخت می‌شود.</small>
                        </div>
                        <i>{allowedPaymentModes.includes('two_stage') ? 'فعال' : 'غیرفعال'}</i>
                      </label>
                    </div>
                    <div className={`admin-service-payment-policy-note ${financialEditLocked ? 'is-locked' : ''}`}>
                      <FiAlertCircle />
                      <span>{paymentPolicyLocked
                        ? 'برای این سفارش پرداخت موفق ثبت شده و روش‌های پرداخت دیگر قابل تغییر نیستند.'
                        : pendingPaymentEditLock.locked
                          ? `نتیجه پرداخت هنوز نهایی نشده است؛ ${paymentLockCountdown(pendingPaymentEditLock.remainingSeconds)} دیگر امکان بررسی و تغییر روش‌ها فعال می‌شود.`
                          : 'حداقل یک روش باید فعال بماند. این محدودیت مستقیماً روی گزینه‌های قابل مشاهده و قابل انتخاب مشتری اعمال می‌شود.'}</span>
                    </div>
                    <button type="button" className="admin-service-payment-policy-submit" onClick={savePaymentPolicy} disabled={busy === 'payment-policy' || financialEditLocked}>
                      <FiCheck /> {busy === 'payment-policy' ? 'در حال ذخیره...' : 'ذخیره روش‌های پرداخت مجاز'}
                    </button>
                  </section>

                  {selected.paymentSchedule?.totalAmount > 0 ? (
                    <section className="admin-service-order-card is-full admin-service-payment-card">
                      <div className="admin-service-order-card-heading">
                        <span><FiCreditCard /></span>
                        <div>
                          <h3>پرداخت و تسویه سفارش</h3>
                          <p>خلاصه مالی قرارداد و پرداخت‌های خارج از درگاه را مدیریت کنید.</p>
                        </div>
                      </div>
                      <div className="admin-service-payment-summary">
                        <div><small>مبلغ قرارداد</small><strong>{formatServiceOrderAmount(selected.paymentSchedule.totalAmount)} {selected.currencyLabel || 'تومان'}</strong></div>
                        <div><small>پرداخت‌شده</small><strong>{formatServiceOrderAmount(selected.paymentSchedule.paidAmount)} {selected.currencyLabel || 'تومان'}</strong></div>
                        <div><small>مانده</small><strong>{formatServiceOrderAmount(selected.paymentSchedule.remainingAmount)} {selected.currencyLabel || 'تومان'}</strong></div>
                        <div><small>وضعیت</small><strong>{paymentStatusLabel(selected.paymentStatus)}</strong></div>
                      </div>

                      {selected.paymentSchedule.mode === 'two_stage' && secondPaymentStage && selected.paymentStatus !== 'paid' && (
                        <div className="admin-service-second-payment">
                          <div>
                            <strong>تسویه نهایی</strong>
                            <small>
                              {secondPaymentStage.status === 'paid'
                                ? 'پرداخت‌شده'
                                : secondPaymentStage.dueType === 'before_delivery' && !secondPaymentStage.dueAt
                                  ? 'پیش از تحویل نهایی'
                                  : 'تاریخ سررسید قابل تنظیم است'}
                            </small>
                          </div>
                          <label>
                            تاریخ سررسید
                            <input type="date" value={editor.secondPaymentDueAt} onChange={(event) => setEditor({ ...editor, secondPaymentDueAt: event.target.value })} />
                          </label>
                        </div>
                      )}

                      {selected.paymentStatus !== 'paid' && (
                        <form className="admin-service-manual-payment" onSubmit={recordManualPayment}>
                          <div className="admin-service-manual-payment-heading">
                            <strong>ثبت پرداخت دستی</strong>
                            <small>برای کارت‌به‌کارت یا واریزی که خارج از سایت انجام شده است.</small>
                          </div>
                          <label>
                            مبلغ پرداخت‌شده ({selected.currencyLabel || 'تومان'})
                            <input type="text" inputMode="numeric" value={manualPayment.amount} onChange={(event) => setManualPayment({ ...manualPayment, amount: event.target.value })} />
                          </label>
                          <label>
                            تاریخ پرداخت
                            <input type="date" value={manualPayment.paidAt} onChange={(event) => setManualPayment({ ...manualPayment, paidAt: event.target.value })} />
                          </label>
                          <label>
                            روش پرداخت
                            <select value={manualPayment.paymentMethod} onChange={(event) => setManualPayment({ ...manualPayment, paymentMethod: event.target.value })}>
                              <option value="card_to_card">کارت‌به‌کارت</option>
                              <option value="bank_transfer">واریز بانکی</option>
                              <option value="cash">نقدی</option>
                              <option value="other">سایر</option>
                            </select>
                          </label>
                          <label>
                            شماره پیگیری
                            <input value={manualPayment.reference} onChange={(event) => setManualPayment({ ...manualPayment, reference: event.target.value })} placeholder="اختیاری" />
                          </label>
                          <label className="is-wide">
                            توضیح برای کاربر
                            <textarea rows="2" value={manualPayment.note} onChange={(event) => setManualPayment({ ...manualPayment, note: event.target.value })} placeholder="اختیاری" />
                          </label>
                          <button type="submit" disabled={busy === 'manual-payment'}>
                            <FiCheck /> {busy === 'manual-payment' ? 'در حال ثبت...' : 'تأیید و ثبت پرداخت'}
                          </button>
                        </form>
                      )}
                    </section>
                  ) : (
                    <div className="admin-service-finance-empty">
                      <FiCreditCard />
                      <div>
                        <strong>هنوز قرارداد مالی فعالی ثبت نشده است</strong>
                        <span>پیشنهاد قیمت را تکمیل و برای مشتری ارسال کنید تا برنامهٔ پرداخت سفارش ایجاد شود.</span>
                      </div>
                    </div>
                  )}

                  <section className="admin-service-order-card is-full admin-service-quote-editor">
                    <div className="admin-service-order-card-heading">
                      <span><FiSend /></span>
                      <div>
                        <h3>پیشنهاد قیمت</h3>
                        <p>مبلغ، زمان اجرا و شرایط پیشنهادی را برای مشتری ارسال کنید.</p>
                      </div>
                    </div>
                    <div className="admin-service-order-fields">
                      <label>
                        مبلغ نهایی ({selected.currencyLabel || 'تومان'})
                        <input type="text" inputMode="numeric" value={editor.quote.amount} onChange={(event) => setEditor({ ...editor, quote: { ...editor.quote, amount: event.target.value } })} />
                      </label>
                      <label>
                        مدت اجرا
                        <input value={editor.quote.duration} onChange={(event) => setEditor({ ...editor, quote: { ...editor.quote, duration: event.target.value } })} placeholder="مثلاً ۳۰ روز کاری" />
                      </label>
                      <label>
                        اعتبار پیشنهاد
                        <input type="date" value={editor.quote.validUntil} onChange={(event) => setEditor({ ...editor, quote: { ...editor.quote, validUntil: event.target.value } })} />
                      </label>
                      <label className="is-wide">
                        شرح پیشنهاد
                        <textarea rows="5" value={editor.quote.details} onChange={(event) => setEditor({ ...editor, quote: { ...editor.quote, details: event.target.value } })} placeholder="دامنه کار، خروجی‌ها و شرایط مهم" />
                      </label>
                    </div>
                    <label className={`admin-service-quote-approval ${editor.quote.requiresCustomerApproval ? '' : 'is-direct-payment'}`}>
                      <input
                        type="checkbox"
                        checked={!editor.quote.requiresCustomerApproval}
                        onChange={(event) => setEditor({
                          ...editor,
                          quote: { ...editor.quote, requiresCustomerApproval: !event.target.checked },
                        })}
                      />
                      <span className="admin-service-quote-approval-check"><FiCheck /></span>
                      <span className="admin-service-quote-approval-copy">
                        <strong>بدون نیاز به تأیید مشتری، مستقیماً آماده پرداخت شود</strong>
                        <small>با فعال‌کردن این گزینه، مشتری دکمه «تأیید پیشنهاد قیمت» نمی‌بیند و مبلغ بلافاصله در پرداخت‌های قابل انجام او قرار می‌گیرد.</small>
                      </span>
                      <i>{editor.quote.requiresCustomerApproval ? 'نیازمند تأیید' : 'پرداخت مستقیم'}</i>
                    </label>
                    <button type="button" className="admin-service-quote-submit" onClick={() => save({ sendQuote: true })} disabled={busy === 'quote'}>
                      <FiSend /> {busy === 'quote' ? 'در حال ارسال...' : editor.quote.requiresCustomerApproval ? 'ارسال برای تأیید مشتری' : 'ارسال و فعال‌سازی پرداخت'}
                    </button>
                  </section>
                </div>
              </section>

              <section
                id="admin-service-order-panel-request"
                className="admin-service-order-tab-panel"
                role="tabpanel"
                aria-labelledby="admin-service-order-tab-request"
                hidden={activeDetailTab !== 'request'}
              >
                <div className="admin-service-order-panel-heading">
                  <div>
                    <span>اطلاعات ثبت‌شده</span>
                    <h3>درخواست اولیه و فایل‌ها</h3>
                    <p>داده‌های اصلی ارسال‌شده از طرف مشتری بدون مخلوط‌شدن با تغییرات اجرایی.</p>
                  </div>
                  <strong className="admin-service-order-panel-metric">
                    {(selected.attachments || []).length.toLocaleString('fa-IR')} فایل
                  </strong>
                </div>

                <div className="admin-service-order-card-grid">
                  <section className="admin-service-order-card is-half admin-service-request-card">
                    <div className="admin-service-order-card-heading">
                      <span><FiFile /></span>
                      <div>
                        <h3>درخواست اولیه مشتری</h3>
                        <p>اطلاعاتی که هنگام ثبت سفارش دریافت شده است.</p>
                      </div>
                    </div>
                    <p className="is-preline">{selected.brief?.description || 'توضیحی ثبت نشده است.'}</p>
                    <dl>
                      <div><dt>خدمت</dt><dd>{selected.serviceSnapshot?.title || '—'}</dd></div>
                      <div><dt>{selected.planSnapshot?.selectionType === 'quote_request' ? 'نوع درخواست' : 'پلن'}</dt><dd>{selected.planSnapshot?.name || '—'} · {selected.planSnapshot?.priceLabel || 'بدون قیمت اولیه'}</dd></div>
                      <div><dt>روش تماس</dt><dd>{selected.contact?.preferredMethod || '—'}</dd></div>
                      <div><dt>تاریخ مطلوب</dt><dd>{formatServiceOrderDate(selected.brief?.targetDate)}</dd></div>
                      <div><dt>بودجه مشتری</dt><dd>{selected.brief?.budgetRange || 'اعلام نشده'}</dd></div>
                      {(selected.brief?.answers || []).map((answer) => (
                        <div key={answer.fieldId}>
                          <dt>{answer.label}</dt>
                          <dd>{Array.isArray(answer.value) ? answer.value.join('، ') : String(answer.value)}</dd>
                        </div>
                      ))}
                    </dl>
                  </section>

                  <section className="admin-service-order-card is-half">
                    <div className="admin-service-order-card-heading">
                      <span><FiDownload /></span>
                      <div>
                        <h3>فایل‌های سفارش</h3>
                        <p>پیوست‌های ارسال‌شده برای این پروژه را دریافت کنید.</p>
                      </div>
                    </div>
                    {(selected.attachments || []).length ? (
                      <div className="admin-service-order-files">
                        {selected.attachments.map((attachment) => (
                          <button key={attachment._id} type="button" onClick={() => download(attachment)}>
                            <FiFile />
                            <span>
                              <strong>{attachment.originalName}</strong>
                              <small>{(attachment.size / 1024 / 1024).toLocaleString('fa-IR', { maximumFractionDigits: 2 })} MB</small>
                            </span>
                            <FiDownload />
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="admin-service-order-empty-state">
                        <FiPaperclip />
                        <strong>فایلی به سفارش پیوست نشده است</strong>
                        <span>اگر مشتری فایلی ارسال کند، در همین بخش در دسترس خواهد بود.</span>
                      </div>
                    )}
                  </section>
                </div>
              </section>

              <section
                id="admin-service-order-panel-history"
                className="admin-service-order-tab-panel"
                role="tabpanel"
                aria-labelledby="admin-service-order-tab-history"
                hidden={activeDetailTab !== 'history'}
              >
                <div className="admin-service-order-panel-heading">
                  <div>
                    <span>ردیابی تغییرات</span>
                    <h3>تاریخچه سفارش</h3>
                    <p>تمام رویدادهای ثبت‌شده به‌ترتیب زمان، از جدیدترین تغییر.</p>
                  </div>
                  <strong className="admin-service-order-panel-metric">
                    {(selected.timeline || []).length.toLocaleString('fa-IR')} رویداد
                  </strong>
                </div>

                <section className="admin-service-order-card is-full admin-service-history-card">
                  {(selected.timeline || []).length ? (
                    <div className="admin-service-order-timeline">
                      {[...(selected.timeline || [])].reverse().map((item) => (
                        <div key={item._id}>
                          <span />
                          <div>
                            <strong>{item.title}</strong>
                            <p>{item.message}</p>
                            <small>{formatServiceOrderDate(item.createdAt)}</small>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="admin-service-order-empty-state">
                      <FiRefreshCw />
                      <strong>هنوز رویدادی ثبت نشده است</strong>
                      <span>با اولین تغییر وضعیت یا اقدام مالی، تاریخچهٔ سفارش در اینجا ساخته می‌شود.</span>
                    </div>
                  )}
                </section>
              </section>

              <section
                id="admin-service-order-panel-conversation"
                className="admin-service-order-tab-panel"
                role="tabpanel"
                aria-labelledby="admin-service-order-tab-conversation"
                hidden={activeDetailTab !== 'conversation'}
              >
                <div className="admin-service-order-panel-heading">
                  <div>
                    <span>ارتباط مستقیم</span>
                    <h3>گفتگو با مشتری</h3>
                    <p>پیام‌ها و پیوست‌های مربوط به روند اجرای پروژه را از این بخش ارسال کنید.</p>
                  </div>
                  <strong className="admin-service-order-panel-metric">
                    {(selected.messages || []).length.toLocaleString('fa-IR')} پیام
                  </strong>
                </div>

                <section className="admin-service-order-card is-full admin-service-order-conversation">
                  <div className="admin-service-order-messages">
                    {(selected.messages || []).length ? (selected.messages || []).map((item) => (
                      <article key={item._id} className={item.senderRole === 'admin' ? 'is-admin' : 'is-user'}>
                        <header>
                          <strong>{item.senderRole === 'admin' ? 'مدیر' : selected.customer?.name}</strong>
                          <small>{formatServiceOrderDate(item.createdAt)}</small>
                        </header>
                        <p>{item.text}</p>
                      </article>
                    )) : (
                      <div className="admin-service-order-empty-state">
                        <FiMessageSquare />
                        <strong>هنوز گفتگویی آغاز نشده است</strong>
                        <span>اولین پیام را برای مشتری بنویسید تا گفتگو شروع شود.</span>
                      </div>
                    )}
                  </div>
                  <form onSubmit={sendMessage}>
                    <textarea rows="3" value={message} onChange={(event) => setMessage(event.target.value)} placeholder="پیام برای مشتری..." />
                    <div>
                      <label>
                        <FiPaperclip /> پیوست
                        <input type="file" multiple onChange={(event) => setMessageFiles(Array.from(event.target.files || []))} />
                      </label>
                      <small>{messageFiles.map((file) => file.name).join('، ')}</small>
                      <button type="submit" disabled={busy === 'message'}><FiSend /> ارسال پیام</button>
                    </div>
                  </form>
                </section>
              </section>

              <div className="admin-form-actions admin-service-order-actions">
                <button type="button" className="admin-primary-action" onClick={() => save()} disabled={busy === 'save'}>
                  <FiCheck aria-hidden="true" />
                  {busy === 'save' ? 'در حال ذخیره...' : 'ذخیره تغییرات سفارش'}
                </button>
                <button type="button" className="admin-secondary-action" onClick={() => setSelected(null)}>
                  <FiArrowRight aria-hidden="true" />
                  بازگشت به فهرست سفارش‌ها
                </button>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>,
    document.body
  );

  return (
    <div className="admin-service-orders">
      <div className="panel-header-section">
        <div className="panel-heading">
          <span className="panel-heading-icon"><FiBriefcase /></span>
          <div>
            <h2>سفارش‌های خدمات</h2>
            <p>{orders.length.toLocaleString('fa-IR')} درخواست برای بررسی، قیمت‌گذاری و اجرا ثبت شده است.</p>
          </div>
        </div>
        <button type="button" className="add-btn" onClick={load}><FiRefreshCw /> به‌روزرسانی</button>
      </div>

      {notice && !selected && (
        <div className={`admin-service-order-notice is-${notice.type}`}><FiAlertCircle /> {notice.text}</div>
      )}

      <div className="admin-service-order-filters">
        <label>
          <FiSearch />
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="کد سفارش، مشتری، خدمت یا پروژه..." />
        </label>
        <label>
          <FiFilter />
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="">همه وضعیت‌ها</option>
            {Object.entries(SERVICE_ORDER_STATUSES).map(([id, info]) => <option key={id} value={id}>{info.label}</option>)}
          </select>
        </label>
      </div>

      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>سفارش</th>
              <th>مشتری</th>
              <th>خدمت / انتخاب</th>
              <th>زمان پشتیبانی</th>
              <th>پروژه</th>
              <th>پرداخت</th>
              <th>وضعیت</th>
              <th>تاریخ</th>
              <th>عملیات</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="9" className="admin-empty-cell">در حال دریافت...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan="9" className="admin-empty-cell">سفارشی یافت نشد.</td></tr>
            ) : filtered.map((order) => {
              const supportTiming = getSupportSubscriptionTiming(order);
              return (
                <tr key={order._id}>
                  <td><strong>#{serviceOrderId(order)}</strong></td>
                  <td>
                    <strong>{order.customer?.name}</strong>
                    <small className="admin-cell-subtitle">{order.customer?.phone || order.customer?.email}</small>
                  </td>
                  <td>
                    {order.serviceSnapshot?.title}
                    <small className="admin-cell-subtitle">{order.planSnapshot?.name}</small>
                  </td>
                  <td>
                    {supportTiming ? (
                      <span className={`admin-service-support-time is-${supportTiming.status}`}>
                        <FiClock />
                        <span>
                          <strong>{supportTimingLabel(supportTiming)}</strong>
                          <small>تا {formatServiceOrderDate(supportTiming.endsAt)}</small>
                        </span>
                      </span>
                    ) : <span className="admin-muted">—</span>}
                  </td>
                  <td>
                    {order.brief?.projectTitle}
                    <small className="admin-cell-subtitle">{Number(order.progress || 0).toLocaleString('fa-IR')}٪ پیشرفت</small>
                  </td>
                  <td>
                    {order.paymentSchedule?.totalAmount
                      ? `${formatServiceOrderAmount(order.paymentSchedule.paidAmount)} از ${formatServiceOrderAmount(order.paymentSchedule.totalAmount)} ${order.currencyLabel || 'تومان'}`
                      : order.quote?.amount
                        ? `${formatServiceOrderAmount(order.quote.amount)} ${order.currencyLabel || 'تومان'}`
                        : 'ثبت نشده'}
                    <small className="admin-cell-subtitle">{paymentStatusLabel(order.paymentStatus)}</small>
                  </td>
                  <td>
                    <span className={`service-order-status is-${statusInfo(order.status).tone}`}>
                      {statusInfo(order.status).label}
                    </span>
                  </td>
                  <td>{formatServiceOrderDate(order.createdAt)}</td>
                  <td>
                    <div className="admin-service-order-row-actions">
                      <button type="button" className="edit-btn" onClick={() => open(order)}>بررسی و مدیریت</button>
                      <button type="button" className="admin-service-order-delete-row" onClick={() => remove(order)} disabled={busy === `delete:${order._id}`} aria-label="حذف کامل سفارش">
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {detailModal}
    </div>
  );
};

export default AdminServiceOrders;
